/* eslint-disable @typescript-eslint/no-explicit-any */
// @ts-nocheck
/**
 * ErrorBoundary — catches render/hook errors in a subtree and surfaces them.
 *
 * Previously, ActivityView crashes fell off the React tree leaving a blank
 * page (session 5.8 / 5.9 regression). Wrapping the view in this boundary
 * forces the crash to be visible in the DOM so assertion tests and humans
 * can see what broke.
 */
import React from 'react';

interface Props {
  children: React.ReactNode;
  label?: string;
}

interface State {
  error: Error | null;
  info: string | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { error, info: null };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    this.setState({ error, info: info.componentStack || null });
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', this.props.label || '', error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="min-h-dvh bg-bg-base text-fg-primary p-6">
          <div className="max-w-4xl mx-auto rounded-lg border border-status-danger-bg/40 bg-status-danger-bg/20 p-6">
            <h1 className="text-3xl font-bold text-status-danger mb-2">Render error</h1>
            <p className="text-fg-secondary mb-4 text-sm">
              {this.props.label ? `in ${this.props.label}` : 'This view crashed while rendering.'}
            </p>
            <pre className="text-xs text-status-danger whitespace-pre-wrap bg-black/30 p-3 rounded border border-status-danger-bg/20">
{String(this.state.error?.stack || this.state.error?.message || this.state.error)}
            </pre>
            {this.state.info && (
              <details className="mt-3 text-xs text-fg-muted">
                <summary className="cursor-pointer">Component stack</summary>
                <pre className="whitespace-pre-wrap">{this.state.info}</pre>
              </details>
            )}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
